import { useMemo } from 'react'

import { useEmployer } from 'pages/employer_onboarding/EmployerProvider'
import { useSelfServeExplorerContext } from 'pages/employer_onboarding/quote/QuoteSelfServeExplorerContext'
import { AllowanceStrategies } from 'pages/employer_onboarding/quote/types'
import { calculateEmployeeMonthlyAllowanceForEmployeeOnly, flatAllowanceIsAffordable, planIsAffordable } from 'utils/quote_calculations'
import { type QuotingQuoteResult } from 'utils/web_api.schemas'

const FAIR_ALLOWANCE_YOUNGEST_OLDEST_RATIO = 3

export interface ComplianceCheckResult {
  hasComplianceIssues: boolean
  employeesWithUnaffordablePlans: QuotingQuoteResult[]
  ratingAreasWithUnfairAllowances: [string, { youngest: QuotingQuoteResult, oldest: QuotingQuoteResult }][]
  shouldDisableReviewButton: boolean
}

export function useQuoteComplianceChecks (): ComplianceCheckResult {
  const { employer } = useEmployer()
  const { allowanceStrategy, eligibleEmployees, filterValues, getMonthlyAllowanceForEmployee } = useSelfServeExplorerContext()

  const isDynamic = allowanceStrategy === AllowanceStrategies.Smart

  const employeesWithUnaffordablePlans = useMemo(() => {
    if (!eligibleEmployees) return []

    return eligibleEmployees.filter((result) => {
      if (isDynamic) {
        return !planIsAffordable(result, {
          employerEmployeeContribution: filterValues.employerEmployeeContribution,
        })
      } else {
        const monthlyAllowance = getMonthlyAllowanceForEmployee(result.id)
        return !flatAllowanceIsAffordable(result, monthlyAllowance)
      }
    })
  }, [eligibleEmployees, filterValues, isDynamic, getMonthlyAllowanceForEmployee])

  const youngestAndOldestEmployeesByRatingArea = useMemo(() => {
    if (!isDynamic || !eligibleEmployees) return {}

    return eligibleEmployees.reduce<Record<string, { youngest: typeof eligibleEmployees[0], oldest: typeof eligibleEmployees[0] }>>((acc, result) => {
      acc[result.rating_area_code] ||= {
        youngest: result,
        oldest: result
      }
      const area = acc[result.rating_area_code]
      if (new Date(result.date_of_birth) > new Date(area.youngest.date_of_birth)) {
        area.youngest = result
      }
      if (new Date(result.date_of_birth) < new Date(area.oldest.date_of_birth)) {
        area.oldest = result
      }
      return acc
    }, {})
  }, [eligibleEmployees, isDynamic])

  const ratingAreasWithUnfairAllowances = useMemo(() => {
    if (!isDynamic) return []

    return Object.entries(youngestAndOldestEmployeesByRatingArea).filter(([_, employees]) => {
      return (calculateEmployeeMonthlyAllowanceForEmployeeOnly(employees.oldest, 1) / FAIR_ALLOWANCE_YOUNGEST_OLDEST_RATIO) >
        calculateEmployeeMonthlyAllowanceForEmployeeOnly(employees.youngest, 1)
    })
  }, [youngestAndOldestEmployeesByRatingArea, filterValues, isDynamic])

  const hasComplianceIssues = employeesWithUnaffordablePlans.length > 0 || ratingAreasWithUnfairAllowances.length > 0

  const hasMoreThan50Employees = employer?.number_of_employees ? employer.number_of_employees >= 50 : false
  const shouldDisableReviewButton = hasComplianceIssues && hasMoreThan50Employees

  return {
    hasComplianceIssues,
    employeesWithUnaffordablePlans,
    ratingAreasWithUnfairAllowances,
    shouldDisableReviewButton,
  }
}
